import React, { PropsWithChildren } from "react";

import { useWorkOSAuth } from "@worktango/d2c-service/workOSReact";
import { useBooleanFlagValue } from "@worktango/feature-flags-react";

import { Button, Flex, Heading, Select, Spacer } from "@kazoohr/confetti";
import { coalesce, coalesceFirstTruthy } from "@kazoohr/helpers";
import { Link } from "@kazoohr/router";

import {
  GetCurrentUserQuery,
  useGetCurrentUserQuery,
} from "../../../generated/apiHooks";
import { PoweredBy } from "../../static/PoweredBy";

import { OrgProvider, useOrgContext } from "./OrgContext";
import styles from "./constellationWrapper.scss";

export function ConstellationWrapper({ children }: PropsWithChildren) {
  return (
    <OrgProvider>
      <div>
        <ConstellationHeader />
        <Spacer size="medium" />
        <div className={styles["constellation-wrapper"]}>{children}</div>
        <Spacer size="extra-large" />
      </div>
    </OrgProvider>
  );
}

function getName(
  user?: null | {
    email: string;
    firstName?: string | null;
    lastName?: string | null;
  }
) {
  const name = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return coalesceFirstTruthy(name, user?.email);
}

function ConstellationHeader() {
  const { state, user } = useWorkOSAuth();

  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      className={styles["constellation-header"]}
    >
      <Heading level="2">
        <Link style={{ color: "inherit" }} to="/">
          Constellation
        </Link>
      </Heading>
      <PoweredBy />
      <Flex gap="8px" justifyContent="flex-end" alignItems="center">
        {state !== "loading" && user ? (
          <AuthenticatedHeaderContent />
        ) : (
          <UnauthenticatedHeaderContent />
        )}
      </Flex>
    </Flex>
  );
}

type UserOrganizationMemberships =
  GetCurrentUserQuery["currentUser"]["organizationMemberships"];

function isPlatformAdmin(
  currentUser?: GetCurrentUserQuery["currentUser"] | null
) {
  return coalesce(currentUser?.isPlatformAdmin, false);
}

function isOrgAdmin(
  currentUser?: GetCurrentUserQuery["currentUser"] | null,
  selectedOrgId?: string | null
) {
  if (!selectedOrgId) {
    return false;
  }
  return coalesce(
    currentUser?.organizationMemberships.some(
      (org) => org.organization.id === selectedOrgId && org.role === "ADMIN"
    ),
    false
  );
}

function AuthenticatedHeaderContent() {
  const { user, signOut } = useWorkOSAuth();
  const { data } = useGetCurrentUserQuery();
  const { selectedOrgId } = useOrgContext();
  const userName = getName(user);

  // istanbul ignore next - defensive check, user guaranteed truthy by parent
  if (!user) {
    return null;
  }

  const organizationMemberships = coalesce(
    data?.currentUser?.organizationMemberships,
    []
  );

  return (
    <>
      <OrgDisplay organizationMemberships={organizationMemberships} />
      <div>{userName}</div>
      <Spacer size="small" orientation="horizontal" />
      {isPlatformAdmin(data?.currentUser) && (
        <Button to="/admin" linkElement={Link} variant="system">
          Admin
        </Button>
      )}
      {isOrgAdmin(data?.currentUser, selectedOrgId) && (
        <Button to="/organization" linkElement={Link} variant="system">
          Organization
        </Button>
      )}
      <Spacer size="small" orientation="horizontal" />
      <Button onClick={signOut} variant="system">
        Sign Out
      </Button>
    </>
  );
}

const NO_ORG_VALUE = "";

function OrgDisplay({
  organizationMemberships,
}: {
  organizationMemberships: UserOrganizationMemberships;
}) {
  const { selectedOrgId, setSelectedOrgId } = useOrgContext();

  if (organizationMemberships.length === 0) {
    return null;
  }

  const options = [
    { value: NO_ORG_VALUE, label: "No organization" },
    ...organizationMemberships.map((org) => ({
      value: org.organization.id,
      label: org.organization.name,
    })),
  ];

  return (
    <>
      <Select
        id="org-picker"
        data-testid="org-picker"
        selectedOptionValue={coalesce(selectedOrgId, NO_ORG_VALUE)}
        options={options}
        onChange={(option) => setSelectedOrgId(coalesce(option.value, null))}
      />
      <Spacer size="small" orientation="horizontal" />
    </>
  );
}

function UnauthenticatedHeaderContent() {
  const { signIn, signUp } = useWorkOSAuth();
  const isSignUpAllowed = useBooleanFlagValue("allow-signup", false);

  return (
    <>
      <Button onClick={signIn} variant="system">
        Sign In
      </Button>
      {isSignUpAllowed && (
        <Button onClick={signUp} variant="primary">
          Sign Up
        </Button>
      )}
    </>
  );
}
